export const bioContent = {
  nl: {
    seoTitle: 'CV · Rob van Rooij (Rot8ion) · Perpetual Plexus',
    seoDescription:
      'Rob van Rooij, geboren in Eindhoven 1976, werkt sinds 2007 als Rot8ion aan mixed media lichtinstallaties en het netwerk Perpetual Plexus.',
    title: 'CV',
    archiveTitle: 'Archief',
    archiveFrameTitle: 'Archief — Rot8ion',
    archiveBack: '← Terug naar het archiefoverzicht',
    intro: 'Rob van Rooij',
    introDetails: [
      'Geboren in de lichtstad Eindhoven, 1976',
      'Master of Science in Multi Media, Sheffield Hallam University, 2004-2008',
    ],
    portraitAlt: 'Rob van Rooij, kunstenaar achter Rot8ion en Perpetual Plexus',
    paragraphs: [
      'Sinds 2007 werkt Rob van Rooij onder de naam Rot8ion, een verwijzing naar de beweging van licht, geluid, beeld en interactie in zijn werk. Met wortels in het Eindhovense nachtleven heeft zijn werk zich de afgelopen decennia getransformeerd van DJ sets, live geluids improvisaties, soundscapes en vj sets naar mixed media installaties. Hij werkt hierbij voornamelijk met lichtbronnen, hout, arylglas, acrylverf en zelf ontwikkelde hard- en software voor het aansturen van licht, video, geluid, netwerkcommunicatie en datavisualisatie.',
      'Zo (co-)creëerde hij ruim honderd installaties bij onder meer lichtfestival Glow Eindhoven, het Evoluon, Natlab, de Oude Rechtbank en het Parktheater in Eindhoven. Daarnaast stond hij op evenementen en festivals als Festival Ensuite, Funki Bizniz, Extrema, Solar en Hidden Garden. In 2014 co-produceerde hij de eindshow van het Extrema Festival (Eindhoven), nog steeds één van de grootste live surround (8.1) performances ter wereld ooit.',
      'Sinds 2016 krijgt zijn werk steeds vaker een langdurig of zelfs permanent karakter, en ontstaat er een netwerk van lichtsculpturen die onderling met elkaar verbonden zijn. Permanente lichtsculpturen en mixed media installaties bij het scholen als het Stedelijk College Eindhoven en het Strabrecht College Geldrop, en ziekenhuizen als het Cartharina Ziekenhuis in Eindhoven en het Maxima Medisch Centrum in Veldhoven zijn of maakten langdurig deel uit van dit netwerk genaamd Perpetual Plexus.',
    ],
    cvTitle: 'Werk',
  },
  en: {
    seoTitle: 'CV · Rob van Rooij (Rot8ion) · Perpetual Plexus',
    seoDescription:
      'Rob van Rooij, born in Eindhoven in 1976, has worked as Rot8ion since 2007 on mixed media light installations and the Perpetual Plexus network.',
    title: 'CV',
    archiveTitle: 'Archive',
    archiveFrameTitle: 'Archive — Rot8ion',
    archiveBack: '← Back to the archive overview',
    intro: 'Rob van Rooij',
    introDetails: [
      'Born in "the city of light" Eindhoven, 1976',
      'Master of Science in Multi Media, Sheffield Hallam University, 2004-2008',
    ],
    portraitAlt: 'Rob van Rooij, the artist behind Rot8ion and Perpetual Plexus',
    paragraphs: [
      'Since 2007, Rob van Rooij has worked under the name Rot8ion, a reference to the movement of light, sound, imagery, and interaction within his work. Over the past few decades, his practice has evolved from DJ sets, live sound improvisations, soundscapes, and VJ sets into mixed-media installations. His work primarily incorporates light sources, wood, acrylic glass, and acrylic paint, alongside self-developed hardware and software for controlling light, video, sound, network communication, and data visualization.',
      'Additionally, he has performed at events and festivals including Festival Ensuite, Funki Bizniz, Extrema, Solar and Hidden Garden. In 2014, he co-produced the closing show for Extrema Festival (Eindhoven), which remains one of the largest live surround-sound (8.1) performances ever staged.',
      'Since 2016, his work has increasingly taken on a long-term or permanent nature, giving rise to an interconnected network of light sculptures. Permanent light sculptures and mixed-media installations at schools such as Stedelijk College Eindhoven and Strabrecht College Geldrop and hospitals including Catharina Hospital in Eindhoven and Máxima Medical Centre in Veldhoven form part of this ongoing network known as Perpetual Plexus.',
    ],
    cvTitle: 'Work',
  },
} as const;

/**
 * The CV entries are largely English already; only the Dutch connective words
 * differ. Keeping one list guarantees NL and EN never drift apart.
 */
export function localizeCvItem(item: string, lang: 'nl' | 'en'): string {
  if (lang === 'nl') return item;
  return item
    .replace(/ met /g, ' with ')
    .replace(/Openingshow/g, 'Opening show')
    .replace(/-Today/g, '-today');
}

export const cvItems = [
  'Mixed media installation "Proud of...." room met Har Hollands @ Signify, Eindhoven, 2026',
  'Mixed media installation De Wensfabriek 4 met Har Hollands @ Glow Eindhoven, Eindhoven, 2025',
  'Mixed media installation Light over matter 2 met Har Hollands @ Glow Eindhoven, Eindhoven, 2025',
  'Permanent mixed media sculpture Strawberry AIscream @ Strabrecht College Geldrop, 2024',
  'Light & music installation Requiem met Har Hollands @ Glow Eindhoven, Eindhoven, 2023',
  'Mixed media installation De Wensfabriek 3 met Har Hollands @ Glow Eindhoven, Eindhoven, 2023',
  'Light & music installation Berg van Licht met Har Hollands @ Landgraaf, 2022',
  'Video installation Trudo Toren @ Hidden Garden, Eindhoven, 2022',
  'Mixed media installation Nieuwe mutanten met Har Hollands @ Glow Eindhoven, Eindhoven, 2021',
  'Light, video & sound installation De Wensfabriek 2 met Har Hollands @ Glow Eindhoven, 2021',
  'Long term video sculpture Perpetual People II @ Maxima Medisch Centrum Veldhoven 2021-2022',
  'Long term video sculpture Perpetual People I @ Catharina Ziekenhuis Eindhoven, 2021-2022',
  'Long term Mixed Media Installation Perpetual Process @ Natlab Eindhoven, 2020-2021',
  'Light & Projection installations @ Vivre, Oude Rechtbank Eindhoven, 2020',
  'Video installation Video Spheres @ Funki Bizniz, Radio Royaal Eindhoven, 2020',
  'Projection Installation Area 1 @ Arthouse, Lab-1 Eindhoven, 2020',
  'Projection Installation Area 2 @ Arthouse, Lab-1 Eindhoven, 2020',
  'Permanent video sculpture TUUMO @ Fit Factory Eersel, 2020-Today',
  'Mixed media Collider performance @ ITS Congres Openingshow, Evoluon Eindhoven, 2019',
  'Vista College Name Reveal Show @ Rodahal, Kerkrade, 2019',
  'Video installation @ Boekienight, Natlab Eindhoven, 2019',
  'Video installation Video Spheres @ Planet Love, Noordkade Veghel, 2018',
  'Video installation @ Price for writers event, Natlab Eindhoven, 2018',
  'Mixed media installation Shattered Shadows met Har Hollands @ Glow Eindhoven, Eindhoven, 2018',
  'Mixed media installation Karkas met Har Hollands @ Glow Eindhoven, Eindhoven, 2018',
  'Video installation @ Innovation Factory Dutch Design Week, Eindhoven, 2018',
  'Mixed media installation @ Waterzuivering Eindhoven, 2018',
  'Video-installation @ Symposium Transformatie, Natlab Eindhoven, 2018',
  'Video installation @ 25 years of Funki Bizniz, Radio Royaal Eindhoven, 2018',
  'Video Installation @ Nicci French & AJ Finn night, Natlab eindhoven, 2018',
  'Live surround sound & Immersive video Collider performance @ Arthouse Natlab, Eindhoven, 2018',
  'Video Installation @ Boekiebal, Natlab Eindhoven, 2018',
  'Permanent Mixed media sculpture Tijdloos @ Stedelijk College Eindhoven, 2018-Today',
  'Video installation @ Prijs voor Eindhovens Schrijftalent, Natlab Eindhoven, 2018',
  'Light, video & music installation De Wensfabriek 1 met Har Hollands @ Glow Eindhoven, 2018',
  'Mixed media installation Light over matter met Har Hollands @ Glow Eindhoven, Eindhoven, 2018',
  'Light & music installation De Oorsprong met Har Hollands @ Glow Eindhoven, Eindhoven 2017',
  'Light & music installation Dome of light met Har Hollands @ Glow Eindhovenl, Eindhoven, 2017',
  'Video installation @ Specktrem, Van Abbe museum Eindhoven, 2017',
  "Video installation @ La nuit d'Orange Koningsnacht, Eindhoven, 2017",
  'Video installation @ Boekiebal, Natlab Eindhoven, 2017',
  'Video Installation Steampunk @ Festival Ensuite, Oude Rechtbank Eindhoven, 2017',
  'Video installation Retro Clown @ Festival Ensuite, Oude Rechtbank Eindhoven, 2017',
  'Light & music installation AnTUenna met Har Hollands @ Glow Eindhoven, Eindhoven, 2016',
  'Video & light installation @ Festival Ensuite, Oude Rechtbank Eindhoven, 2016',
  'Video installation @ Planet Love ft. Benny Rodriguez, Noordkade Veghel, 2016',
  'Video installation @ Funki Bizniz, Radio Royaal Eindhoven, 2016',
  'Video installation Spheres @ Natlab Eindhoven, 2016',
  "Video installations @ La Nuit d'Orange, Tuchthuys Eindhoven, 2016",
  'Mixed media installation / exposition Perpetual Patterns @ Natlab Eindhoven, 2016',
  'Video installations @ Arthouse Into the Deep, Natlab Eindhoven, 2016',
  'Video installations @ Ensuite Sixteen NYE, Oude Rechtbank Eindhoven, 2016',
  'Video installations @ Arthouse ft Monika Kruse, Natlab Eindhoven, 2016',
  'Mixed media installation Werk in opbouw met Har Hollands @ Glow Eindhoven, Eindhoven, 2015',
  'Video installations @ Festival Ensuite, Oude Rechtbank Eindhoven, 2015',
  'Video installations @ Club Natlab Labdance, Hande Hoch, Natlab Eindhoven 2015',
  'Permanent light installation PLANTetarium @ Oude Rechtbank Eindhoven, 2015-2023',
  'Video installation @ Official afterparty Extrema Outdoor, Ensuite, Eindhoven, 2015',
  'Video installations @ Koningsnacht, Festival Ensuite, Oude Rechtbank Eindhoven, 2015',
  'Video installations @ Festival Ensuite, Oude Rechtbank Eindhoven, 2015',
  'Light & Video show @ De Beste Ondernemers Visie 2015, Parktheater Eindhoven, 2015',
  'Video installation @ Labtanz Berlin, Natlab Eindhoven, 2015',
  'Light and video installations @ Lafete 040 Deep till Dawn, Oude Rechtbank Eindhoven, 2015',
  'Video installations @ Funki Bizniz, Designhuis Eindhoven, 2015',
  'Light installation EFFEX @ Glow Eindhoven, Eindhoven, 2014',
  'Light installation @ Festival Ensuite, Oude Rechtbank Eindhoven, 2014',
  'Video installation @ Club Natlab, The city never sleeps, Natlab, Eindhoven, 2014',
  'Mixed media Collider Final show performance @ Extrema Outdoor, Eindhoven, 2014',
  'Mixed media Collider Labyrithm Area @ Extrema Outdoor 2014, Eindhoven, 2014',
  'Projection installations @ Club Natlab, Natlab Eindhoven, 2014',
  'Mixed media installation Teleportation Tunnel @ BOV Congres, Parktheater Eindhoven, 2014',
  'Visual installation @ Club Natlab, Natlab Eindhoven, 2014',
  'Video installations @ Lafete 040 NYE 2013, Oude Rechtbank Eindhoven, 2014',
  'Video installation Perpetual Pixel @ Glow Eindhoven, Witte Dame, Eindhoven, 2013',
  'Mixed media Collider performance @ JM Congres Evoluon Eindhoven, 2013',
  'Mixed media performance @ Extrema event Dutch Design Week, Eindhoven, 2013',
  'Mixed media Collider performance @ Fontys ACI, Tilburg, 2013',
  'Live surround sound & light area @ Extrema Outdoor, Eindhoven, 2013',
  'Immersive mixed media installation met Collider, @ Expositieruimte, Eindhoven, 2013',
  'Mixed media Collider performance @ Internationale Ondernemerscongres, Evoluon Eindhoven, 2013',
  'Mixed media Collider performance @ Food inspiration days, Evoluon Eindhoven, 2013',
  'Mixed media Collider performance @ TEDx Brainport, Evoluon Eindhoven, 2013',
  'Mixed media Collider performance met Martyn, Erosie @ Gashouder TU Eindhoven, 2013',
  'Mixed media installation Facebooksteeg met Har Hollands @ Gent Light festival, Gent (BE), 2012',
  'Surround sound performance Trom en de Vlag met Bart van Dongen @ Floriade Venlo, 2012',
  'Surround sound performance @ (h)ear festival, Maastricht, 2011',
  'Light & surround sound installation Metropolux met Har Hollands @ Glow Eindhoven, Eindhoven, 2011',
  'Mixed media installation @ STRP Festival, Eindhoven, 2010',
  'Surround sound performance Trom en de Vlag met Bart van Dongen @ Vrijthof Maastricht, 2010',
  'Surround sound performance @ Solar Weekend, Maasplassen Roermond, 2009',
  'Surround sound performance Rockacademie anniversary met Roger Martinez @ 013 Tilburg, 2009',
  'Surround sound performance Horizontal Excursion met Roger Martinez @ 013 Tilburg, 2009',
  'Surround sound performance @ Extrema outdoor, Eindhoven, 2008',
  'Mixed installation Kubik met Modulorbeat & Balestra @ Glow Eindhoven, Eindhoven, 2008',
  'Surround sound performance @ Dutch Design Week, Eindhoven, 2008',
  'Surround sound performance @ Playgrounds Festival, Tilburg, 2007',
] as const;
